/**
 * Cmd/Ctrl+K palette. Desktop keyboard shortcut for the actions that would
 * otherwise take two or three clicks.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  ArrowLeftRight,
  BarChart3,
  HandCoins,
  LayoutDashboard,
  ListOrdered,
  Plus,
  Repeat,
  Settings,
  Wallet,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useFunds } from '@/hooks/useApi';
import type { MovementMode } from '@/components/MovementDialog';

export function CommandPalette({
  onQuickAdd,
  onMovement,
}: {
  onQuickAdd: () => void;
  onMovement: (mode: MovementMode) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: funds = [] } = useFunds();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const run = (fn: () => void) => {
    setOpen(false);
    // Let the dialog finish closing before opening another one, or Radix's
    // focus management fights itself and the second dialog opens unfocused.
    setTimeout(fn, 80);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0" hideClose>
        <DialogTitle className="sr-only">Търсене на команда</DialogTitle>
        <Command
          label="Команди"
          className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          <Command.Input
            placeholder="Търси команда или каса…"
            className="h-12 w-full border-b bg-transparent px-4 text-base outline-none placeholder:text-muted-foreground"
          />
          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              Нищо намерено.
            </Command.Empty>

            <Command.Group heading="Действия">
              <Item icon={Plus} onSelect={() => run(onQuickAdd)}>
                Нова операция
              </Item>
              <Item icon={ArrowLeftRight} onSelect={() => run(() => onMovement('transfer'))}>
                Прехвърляне
              </Item>
              <Item icon={Repeat} onSelect={() => run(() => onMovement('exchange'))}>
                Обмяна на валута
              </Item>
              <Item icon={HandCoins} onSelect={() => run(() => onMovement('loan'))}>
                Заем между каси
              </Item>
            </Command.Group>

            <Command.Group heading="Отиди на">
              <Item icon={LayoutDashboard} onSelect={() => run(() => navigate('/'))}>
                Начало
              </Item>
              <Item icon={ListOrdered} onSelect={() => run(() => navigate('/transactions'))}>
                Операции
              </Item>
              <Item icon={BarChart3} onSelect={() => run(() => navigate('/reports'))}>
                Справки
              </Item>
              <Item icon={Settings} onSelect={() => run(() => navigate('/settings'))}>
                Настройки
              </Item>
            </Command.Group>

            {funds.length > 0 ? (
              <Command.Group heading="Каси">
                {funds.map((f) => (
                  <Item
                    key={f.id}
                    icon={Wallet}
                    onSelect={() => run(() => navigate(`/funds/${f.id}`))}
                  >
                    {f.name}
                  </Item>
                ))}
              </Command.Group>
            ) : null}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function Item({
  icon: Icon,
  children,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  onSelect: () => void;
}): JSX.Element {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2.5 text-sm data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      {children}
    </Command.Item>
  );
}
